/** @jsx jsx */

import { Fragment as _ } from 'react';
import { css, jsx } from '@emotion/core';
import { HotSpotTickedWhenSeen } from './HotSpotTickedWhenSeen';

interface IPolygonProps {
  id: string;
  points: string;
  hotSpotsVisible: boolean;
  r: string | number;
  g: string | number;
  b: string | number;
  hotSpotColor: string;
  hotSpotFocusColor: string;
  highContrast?: boolean;

  onClick: (e: any) => void;
  immediateFeedback?: boolean;
  isCorrect?: boolean;
  enabled?: boolean;
  hasBeenSeen: boolean;
  hotSpotFocus: string | undefined;
  showSeenState?: boolean | 0 | 1;
  inverseScale: number;
  showChecked?: boolean | undefined;
  submitState?: string;
  correctAnswers?: number[] | undefined;
  selected?: boolean | undefined;
}
const Polygon = (props: IPolygonProps) => {
  const {
    id,
    hotSpotsVisible,
    r,
    g,
    b,
    hotSpotColor,
    hotSpotFocusColor,
    highContrast,
    onClick,
    immediateFeedback = true,
    isCorrect = false,
    enabled = true,
    hasBeenSeen,
    showSeenState,
    hotSpotFocus,
    inverseScale,
    showChecked,
    submitState,
    correctAnswers = [],
  } = props;

  let points = props.points;

  let pointsToArray;
  if (points.includes(',')) {
    points = points.replace(/,/g, ' ');
  }

  pointsToArray = points.split(' ');

  const xPoints = pointsToArray.filter((p, i) => {
    return i % 2 < 1;
  });
  const yPoints = pointsToArray.filter((p, i) => {
    return i % 2 > 0;
  });

  const minX: number = Math.min(...xPoints);
  const maxX: number = Math.max(...xPoints);
  const minY: number = Math.min(...yPoints);
  const maxY: number = Math.max(...yPoints);

  const x = minX + (maxX - minX) / 2;
  const y = minY + (maxY - minY) / 2;

  const selected = !immediateFeedback ? props?.selected : null;

  const hasFocus = `hotspot-${hotSpotFocus}` === id;
  const strokeWidth = (hasFocus ? (highContrast ? 5 : 3) : 1) * inverseScale;

  return (
    <svg>
      <polygon
        id={id}
        points={points}
        css={css`
          ${((window.dev && window.dev()) || hotSpotsVisible) &&
            `&:hover {
    fill: rgba(${r}, ${g}, ${b}, 0.25) !important;
  }`}
        `}
        style={
          window.dev()
            ? {
                fill: `rgba(${r}, ${g}, ${b}, 0.5)`,
                stroke: hotSpotColor,
                strokeWidth: 1,
                cursor:
                  (window.dev && window.dev()) || hotSpotsVisible
                    ? 'pointer'
                    : 'auto',
                transition: 'fill 0.1s',
              }
            : {
                fill: 'rgba(0, 0, 0, 0)',
                ...(hotSpotsVisible && {
                  stroke: hasFocus
                    ? hotSpotFocusColor || hotSpotColor
                    : hotSpotColor,
                  strokeWidth,

                  cursor: 'pointer',
                  transition: 'all 0.1s',
                }),

                pointerEvents: !immediateFeedback
                  ? enabled
                    ? 'auto'
                    : 'none'
                  : 'auto',

                // we'll stamp this in here as an ' override '
                // so as not to futz with the logic above that works
                // if we're a quiz. and selected. kill pointer
                ...(!immediateFeedback &&
                  correctAnswers?.length === 1 &&
                  selected && {
                    pointerEvents: 'none',
                  }),
              }
        }
        onClick={onClick}
      />

      {showChecked && (
        <HotSpotTickedWhenSeen
          x={x}
          y={y}
          size={75 * inverseScale}
          color={hotSpotColor}
          inverseScale={inverseScale}
          displayAsRadio={submitState && submitState !== 'resolved'}
        />
      )}
    </svg>
  );
};

export { Polygon };
